/**
 * Chat + conversations API (WP29 / Spec §K).
 */

import {
  chatSseEventSchema,
  conversationDetailSchema,
  conversationListResponseSchema,
  type ChatSseEvent,
  type ConversationDetail,
  type ConversationListResponse,
} from "@carago/shared";
import { applyAccessJwtHeader } from "./accessJwt.js";
import { apiFetch, ApiClientError, type ApiFetchOptions } from "./api.js";
import { getApiBaseUrl } from "./config.js";
import { parseSseStream } from "./sse.js";

export type ListConversationsParams = {
  cursor?: string | null;
  limit?: number;
};

function withQuery(
  path: string,
  params: Record<string, string | number | undefined | null>,
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    qs.set(key, String(value));
  }
  const query = qs.toString();
  return query ? `${path}?${query}` : path;
}

function joinUrl(base: string, path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (!base) return normalized;
  return `${base}${normalized}`;
}

export async function listConversations(
  params: ListConversationsParams = {},
  options?: ApiFetchOptions,
): Promise<ConversationListResponse> {
  const raw = await apiFetch<unknown>(
    withQuery("/v1/conversations", {
      cursor: params.cursor,
      limit: params.limit,
    }),
    { method: "GET", ...options },
  );
  return conversationListResponseSchema.parse(raw);
}

export async function getConversation(
  id: string,
  options?: ApiFetchOptions,
): Promise<ConversationDetail> {
  const raw = await apiFetch<unknown>(`/v1/conversations/${id}`, {
    method: "GET",
    ...options,
  });
  return conversationDetailSchema.parse(raw);
}

export type StreamChatParams = {
  message: string;
  conversationId?: string | null;
  signal?: AbortSignal;
  onEvent: (event: ChatSseEvent) => void;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  accessJwt?: string | null;
  getAccessJwt?: () => Promise<string | null>;
};

/**
 * POST /v1/chat/stream and dispatch typed SSE events.
 * Uses fetch + ReadableStream (not EventSource) for POST + credentials.
 */
export async function streamChatMessage(
  params: StreamChatParams,
): Promise<void> {
  const fetchImpl = params.fetchImpl ?? fetch;
  const baseUrl = params.baseUrl ?? getApiBaseUrl();
  const headers = new Headers({
    Accept: "text/event-stream",
    "Content-Type": "application/json",
  });
  await applyAccessJwtHeader(headers, {
    accessJwt: params.accessJwt,
    getAccessJwt: params.getAccessJwt,
  });

  const response = await fetchImpl(joinUrl(baseUrl, "/v1/chat/stream"), {
    method: "POST",
    credentials: "include",
    headers,
    signal: params.signal,
    body: JSON.stringify({
      message: params.message,
      ...(params.conversationId
        ? { conversation_id: params.conversationId }
        : {}),
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    let message = `Request failed (${response.status})`;
    let code = "PROVIDER_ERROR";
    try {
      const parsed = JSON.parse(text) as {
        error?: { code?: string; message?: string };
      };
      if (parsed.error?.message) message = parsed.error.message;
      if (parsed.error?.code) code = parsed.error.code;
    } catch {
      if (text) message = text.slice(0, 200);
    }
    throw new ApiClientError(response.status, code, message);
  }

  if (!response.body) {
    throw new ApiClientError(
      502,
      "PROVIDER_ERROR",
      "Chat stream returned empty body",
    );
  }

  for await (const frame of parseSseStream(response.body)) {
    let json: unknown;
    try {
      json = JSON.parse(frame.data) as unknown;
    } catch {
      continue;
    }
    const parsed = chatSseEventSchema.safeParse(json);
    if (!parsed.success) continue;
    params.onEvent(parsed.data);
  }
}

/** Extract research_job_id from a tool_result summary JSON string. */
export function extractResearchJobId(summary: string): string | null {
  try {
    const parsed = JSON.parse(summary) as { research_job_id?: unknown };
    return typeof parsed.research_job_id === "string"
      ? parsed.research_job_id
      : null;
  } catch {
    return null;
  }
}

export function researchJobPath(jobId: string): string {
  return `/research-jobs/${jobId}`;
}
